/**
 * SGA · PGA 가 **자동으로 관리되는지, 손으로 정한 크기인지** — 노드 하나의 메모리 파라미터(GV$PARAMETER)로 판정한다.
 * (자원 분석, 2026-10-03 요청) 「PGA 목표가 0」 처럼 값만 보면 뜻을 알 수 없는 것을 화면이 말하게 하려는 것이다.
 *
 * 판정 규칙(오라클 문서 기준):
 *  - MEMORY_TARGET > 0                → AMM. SGA·PGA 를 오라클이 오간다. SGA_TARGET·PGA_AGGREGATE_TARGET 은 최소치.
 *  - 아니고 SGA_TARGET > 0            → SGA 는 ASMM(구성 요소 자동 조정).
 *  - 둘 다 0                          → SGA 수동(구성 요소 파라미터로 고정).
 *  - WORKAREA_SIZE_POLICY = MANUAL   → PGA 수동(SORT_AREA_SIZE·HASH_AREA_SIZE). PGA_AGGREGATE_TARGET 을 0 으로 두면 오라클이 이렇게 둔다.
 *  - 아니고 AMM                       → PGA 도 AMM 이 정한다(목표가 0 이면 기동 때 약 40%).
 *  - 아니고 PGA_AGGREGATE_TARGET > 0  → PGA 자동(목표 기준 배분).
 * 값이 없는 파라미터(옛 판에 없는 MEMORY_TARGET·PGA_AGGREGATE_LIMIT)는 0/없음으로 본다.
 */

export const MEMORY_PARAM_NAMES = [
    'memory_target', 'memory_max_target', 'sga_target', 'sga_max_size',
    'pga_aggregate_target', 'pga_aggregate_limit', 'workarea_size_policy',
];

const MB = 1024 * 1024;
const toMb = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n / MB : null;
};

/**
 * @param {Array<{name:string, value:string}>} params 한 노드의 행
 * @returns {null | {values:object, sga:{mode:string, notes:string[]}, pga:{mode:string, notes:string[]}}}
 *          파라미터를 하나도 못 읽었으면 null(권한 없음 — 「수동」 이라고 넘겨짚지 않는다)
 */
export function memoryModeOf(params) {
    const list = Array.isArray(params) ? params : [];
    if (list.length === 0) return null;
    const raw = {};
    list.forEach((p) => { if (p && p.name) raw[String(p.name).toLowerCase()] = p.value; });

    const values = {
        memoryTarget: toMb(raw.memory_target) || 0,
        memoryMaxTarget: toMb(raw.memory_max_target) || 0,
        sgaTarget: toMb(raw.sga_target) || 0,
        sgaMaxSize: toMb(raw.sga_max_size) || 0,
        pgaTarget: toMb(raw.pga_aggregate_target) || 0,
        // 한도는 「없음(옛 판)」 과 「0 = 한도 없음」 이 다르다
        pgaLimit: toMb(raw.pga_aggregate_limit),
        workareaPolicy: String(raw.workarea_size_policy || 'AUTO').toUpperCase(),
    };
    const amm = values.memoryTarget > 0;

    const sga = amm
        ? { mode: 'amm', notes: values.sgaTarget > 0 ? ['ammMin'] : [] }
        : values.sgaTarget > 0 ? { mode: 'asmm', notes: [] } : { mode: 'manual', notes: [] };
    if (values.sgaMaxSize > 0) sga.notes.push('max');

    let pga;
    if (values.workareaPolicy === 'MANUAL') {
        pga = { mode: 'manual', notes: [] };
        if (values.pgaTarget === 0 && !amm) pga.notes.push('targetZero');
        if (values.pgaTarget > 0) pga.notes.push('policyManualWithTarget');
    } else if (amm) {
        pga = { mode: 'amm', notes: [values.pgaTarget > 0 ? 'ammMin' : 'ammZero'] };
    } else if (values.pgaTarget > 0) {
        pga = { mode: 'auto', notes: [] };
    } else {
        // 목표 0 · 정책 AUTO 로 보고되는 경우 — 실제로는 수동으로 돈다
        pga = { mode: 'manual', notes: ['targetZero'] };
    }
    if (values.pgaLimit === 0) pga.notes.push('limitNone');
    else if (values.pgaLimit > 0) pga.notes.push('limit');

    return { values, sga, pga };
}

/** 노드별로 묶는다 — {instId: rows} */
export function groupParamsByInst(rows) {
    const out = new Map();
    (rows || []).forEach((r) => {
        const k = r.instId ?? 1;
        if (!out.has(k)) out.set(k, []);
        out.get(k).push(r);
    });
    return out;
}
